import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

import Puerta from './components/Puerta';
import Panel from './components/Panel';
import Inmuebles from './components/Inmuebles';
import Contratos from './components/Contratos';
import Cobrar from './components/Cobrar';
import Arrendatarios from './components/Arrendatarios';
import Gastos from './components/Gastos';
import Rendimiento from './components/Rendimiento';

// El menú, en el orden en que ocurre el negocio: entra la mercancía, se vende,
// se mira qué dejó. Que el menú siga la cadena real ahorra explicar por dónde
// se empieza.
const PANTALLAS = [
  { grupo: 'Cada día' },
  { id: 'panel',     nombre: 'Panel',      ico: '📊', Componente: Panel },
  { id: 'cobrar',    nombre: 'Cobrar',     ico: '📥', Componente: Cobrar, alertas: true },
  { id: 'inmuebles', nombre: 'Inmuebles',  ico: '🏢', Componente: Inmuebles },
  { grupo: 'Los contratos' },
  { id: 'contratos', nombre: 'Contratos',  ico: '📄', Componente: Contratos },
  { id: 'arrendatarios', nombre: 'Arrendatarios', ico: '👥', Componente: Arrendatarios },
  { grupo: 'La plata' },
  { id: 'gastos',    nombre: 'Gastos',     ico: '🔧', Componente: Gastos },
  { id: 'rendimiento', nombre: 'Rendimiento', ico: '💰', Componente: Rendimiento },
];

const CLAVE_SESION = 'arriendos-sesion';

// El token se aplica de forma SÍNCRONA, no dentro de un efecto. React ejecuta
// los efectos de los hijos antes que los del padre, así que un efecto aquí
// llegaría tarde: la primera pantalla ya habría pedido sus datos sin cabecera y
// recibido un 401 que cierra la sesión recién abierta.
function aplicarToken(token) {
  if (token) axios.defaults.headers.common.Authorization = `Bearer ${token}`;
  else delete axios.defaults.headers.common.Authorization;
  return token;
}

function leerSesion() {
  try {
    const g = JSON.parse(localStorage.getItem(CLAVE_SESION));
    aplicarToken(g?.token);
    return g;
  } catch { return null; }
}

function App() {
  const [sesion, setSesion] = useState(leerSesion);
  const [pantalla, setPantalla] = useState('panel');
  const [alertas, setAlertas] = useState(0);

  const salir = useCallback(() => {
    localStorage.removeItem(CLAVE_SESION);
    aplicarToken(null);
    setSesion(null);
    setPantalla('panel');
  }, []);

  // Un token vencido deja la aplicación devolviendo 401 en todas las pantallas
  // sin decir por qué. Se atrapa una vez aquí y se vuelve a la entrada.
  useEffect(() => {
    const id = axios.interceptors.response.use(
      (r) => r,
      (err) => {
        if (err.response?.status === 401 && sesion) salir();
        return Promise.reject(err);
      }
    );
    return () => axios.interceptors.response.eject(id);
  }, [sesion, salir]);

  // Cuántos productos están agotados o por debajo del mínimo. Va en el menú
  // para que se vea desde cualquier pantalla: una alerta que hay que ir a
  // buscar no es una alerta.
  const contarAlertas = useCallback(() => {
    if (!sesion) return;
    axios.get('/api/cobrar')
      .then(r => setAlertas((r.data?.contratos || []).length))
      .catch(() => {});
  }, [sesion]);

  useEffect(contarAlertas, [contarAlertas, pantalla]);

  const entrar = (datos) => {
    localStorage.setItem(CLAVE_SESION, JSON.stringify(datos));
    aplicarToken(datos.token);
    setSesion(datos);
  };

  if (!sesion) return <Puerta onEntrar={entrar} />;

  const actual = PANTALLAS.find(p => p.id === pantalla) || PANTALLAS[1];
  const Componente = actual.Componente;

  return (
    <div className="armazon">
      <nav className="menu">
        <div className="marca">
          <span className="ico">🏢</span>
          <div>
            <b>Arriendos</b>
            <span>{sesion.user?.negocio}</span>
          </div>
        </div>

        {PANTALLAS.map((p, i) => p.grupo
          ? <div key={'g' + i} className="grupo">{p.grupo}</div>
          : (
            <button
              key={p.id}
              className={'opcion' + (pantalla === p.id ? ' activa' : '')}
              onClick={() => setPantalla(p.id)}
            >
              <span className="ico">{p.ico}</span>
              {p.nombre}
              {p.alertas && alertas > 0 && <span className="aviso-num">{alertas}</span>}
            </button>
          )
        )}

        

        {/* El manual, en una pestaña aparte para poder leerlo con el programa
            abierto al lado. Va abajo y siempre visible: quien lo necesita es
            justo quien no sabe dónde buscarlo. */}
        <a className="opcion" href="/manual/" target="_blank" rel="noopener noreferrer">
          <span className="ico">📖</span>
          Manual
        </a>

        <button className="opcion salir" onClick={salir}>
          <span className="ico">🚪</span>
          Salir
        </button>
      </nav>

      <main className="lienzo">
        <Componente irA={setPantalla} onCambio={contarAlertas} />
      </main>
    </div>
  );
}

export default App;
